import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import GlobeVisualization from '../components/GlobeVisualization';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Globe2, MapPin, Navigation, Crosshair, Ship } from 'lucide-react';

const GlobePage = () => {
  const [layers, setLayers] = useState({
    depth: true,
    temperature: true,
    salinity: false,
  });

  const [vesselData, setVesselData] = useState({
    lat: -23.745,
    lon: -134.120,
    speed: 9.8,
    heading: 212,
  });

  const [nearbyVessels, setNearbyVessels] = useState([]);
  const [vesselsDataSource, setVesselsDataSource] = useState('Loading...');

  // Fetch nearby vessels
  useEffect(() => {
    const fetchNearbyVessels = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/vessels/nearby?lat=${vesselData.lat}&lon=${vesselData.lon}&radius=50`);
        const result = await response.json();
        
        if (result.success && result.data) {
          setNearbyVessels(result.data.vessels);
          setVesselsDataSource(result.source);
        }
      } catch (error) {
        console.error('Error fetching nearby vessels:', error);
        setVesselsDataSource('Not Available');
      }
    };

    fetchNearbyVessels();
    // Refresh every 2 minutes
    const interval = setInterval(fetchNearbyVessels, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [vesselData.lat, vesselData.lon]);

  // Simulate real-time updates for own vessel
  useEffect(() => {
    const interval = setInterval(() => {
      setVesselData(prev => ({
        ...prev,
        lat: prev.lat + (Math.random() - 0.5) * 0.001,
        lon: prev.lon + (Math.random() - 0.5) * 0.001,
        speed: prev.speed + (Math.random() - 0.5) * 0.2,
        heading: (prev.heading + (Math.random() - 0.5) * 2 + 360) % 360,
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const toggleLayer = (layer) => {
    setLayers(prev => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  };

  return (
    <div 
      className="grid grid-cols-1 lg:grid-cols-[auto_1fr] lg:grid-rows-[56px_1fr] min-h-screen bg-background text-foreground"
      data-testid="globe-page-container"
    >
      <Sidebar layers={layers} toggleLayer={toggleLayer} currentRoute="/globe" />
      <Topbar vesselData={vesselData} />
      <main className="pt-14 lg:pt-0 lg:col-start-2 lg:row-start-2 p-4 md:p-6 relative z-0">
        <div className="mb-6" data-testid="page-header">
          <div className="flex items-center gap-3 mb-2">
            <Globe2 className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">3D Globe Visualization</h1>
          </div>
          <p className="text-muted-foreground">Real-time vessel tracking and oceanographic data visualization</p>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="globe-page-grid">
          <GlobeVisualization vesselData={vesselData} layers={layers} />
          
          <Card className="col-span-12 xl:col-span-5" data-testid="vessel-details-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Vessel Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div data-testid="vessel-detail-lat">
                  <p className="text-xs text-muted-foreground mb-1">Latitude</p>
                  <p className="text-lg font-medium font-mono">{vesselData.lat.toFixed(6)}°</p>
                </div>
                <div data-testid="vessel-detail-lon">
                  <p className="text-xs text-muted-foreground mb-1">Longitude</p>
                  <p className="text-lg font-medium font-mono">{vesselData.lon.toFixed(6)}°</p>
                </div>
                <div data-testid="vessel-detail-speed">
                  <p className="text-xs text-muted-foreground mb-1">Speed</p>
                  <p className="text-lg font-medium font-mono">{vesselData.speed.toFixed(2)} kn</p>
                </div>
                <div data-testid="vessel-detail-heading">
                  <p className="text-xs text-muted-foreground mb-1">Heading</p>
                  <p className="text-lg font-medium font-mono">{Math.round(vesselData.heading)}°</p>
                </div>
              </div>

              <div className="pt-4 border-t border-border/40">
                <h4 className="text-sm font-medium mb-3">Active Data Layers</h4>
                <div className="flex flex-wrap gap-2" data-testid="active-layers">
                  {Object.entries(layers).map(([key, value]) => (
                    value && (
                      <Badge key={key} variant="outline" data-testid={`active-layer-${key}`}>
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </Badge>
                    )
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-border/40">
                <h4 className="text-sm font-medium mb-3">Nearby Vessels</h4>
                <p className="text-xs text-muted-foreground mb-2">Source: {vesselsDataSource}</p>
                {nearbyVessels.length > 0 ? (
                  <div className="space-y-2" data-testid="nearby-vessels-list">
                    {nearbyVessels.slice(0, 5).map((vessel, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded bg-secondary/20 border border-border/30" data-testid={`nearby-vessel-${idx}`}>
                        <div className="flex items-center gap-2">
                          <Ship className="h-3 w-3 text-primary" />
                          <span className="text-xs font-mono">{vessel.name || 'Unknown'}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {vessel.distance ? `${vessel.distance.toFixed(1)} nm` : 'N/A'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="no-vessels">No vessels detected in range</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-12" data-testid="navigation-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Navigation className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Navigation Status</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div data-testid="nav-status-gps">
                  <p className="text-xs text-muted-foreground mb-1">GPS Status</p>
                  <Badge variant="outline" className="border-emerald-400 text-emerald-400 bg-emerald-500/10">
                    Active
                  </Badge>
                </div>
                <div data-testid="nav-status-autopilot">
                  <p className="text-xs text-muted-foreground mb-1">Autopilot</p>
                  <Badge variant="outline" className="border-emerald-400 text-emerald-400 bg-emerald-500/10">
                    Engaged
                  </Badge>
                </div>
                <div data-testid="nav-status-ais">
                  <p className="text-xs text-muted-foreground mb-1">AIS Transponder</p>
                  <Badge variant="outline" className="border-emerald-400 text-emerald-400 bg-emerald-500/10">
                    Broadcasting
                  </Badge>
                </div>
                <div data-testid="nav-status-weather">
                  <p className="text-xs text-muted-foreground mb-1">Weather Link</p>
                  <Badge variant="outline" className="border-emerald-400 text-emerald-400 bg-emerald-500/10">
                    Connected
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default GlobePage;
